import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import OnboardingShell from '@/components/OnboardingShell';
import PhoneVerification from '@/components/phone-verification';
import { ImageUpload } from '@/components/image-upload';
import { AVATAR_OPTIONS } from '@/components/avatar-selection';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest } from '@/lib/queryClient';
import { getErrorMessage } from '@/lib/errors';

function avatarUrlFromType(avatarType?: string | null, fallbackUrl?: string | null) {
  const fromBank = AVATAR_OPTIONS.find((a) => a.id === avatarType);
  return fromBank?.image || fallbackUrl || '';
}

export default function EditProfile() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const [phone, setPhone] = useState('');
  const [savedPhone, setSavedPhone] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [avatarType, setAvatarType] = useState('');
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [saving, setSaving] = useState(false);

  // Read-only identity
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [avatarName, setAvatarName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['/api/profile'],
    enabled: isAuthenticated,
    retry: false,
  });

  useEffect(() => {
    if (!profileData) return;
    const p = profileData as Record<string, unknown>;
    setFirstName(String(p.firstName || ''));
    setLastName(String(p.lastName || ''));
    setAvatarName(String(p.avatarName || ''));
    setAge(p.age != null ? String(p.age) : '');
    setGender(String(p.gender || ''));
    const phoneVal = String(p.phoneNumber || '');
    setPhone(phoneVal);
    setSavedPhone(phoneVal);
    setPhoneVerified(Boolean(p.isPhoneVerified));
    const type = String(p.avatarType || '');
    setAvatarType(type);
    setProfilePictureUrl(
      avatarUrlFromType(type, String(p.profilePictureUrl || p.profileImageUrl || ''))
    );
  }, [profileData]);

  const phoneChanged = phone.trim() !== savedPhone.trim();
  const canSavePhone = !phoneChanged || phoneVerified;

  const close = () => setLocation('/profile');

  const onPhotoChange = (url: string) => {
    setProfilePictureUrl(url);
    setAvatarType(url ? 'custom' : 'default');
  };

  const save = async () => {
    if (phoneChanged && !phoneVerified) {
      toast({
        variant: 'destructive',
        title: 'Verify phone',
        description: 'Complete OTP verification before saving a new phone number.',
      });
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        avatarType: avatarType || (profilePictureUrl ? 'custom' : 'default'),
        profilePictureUrl: profilePictureUrl || undefined,
      };
      if (phone.trim()) {
        payload.phoneNumber = phone.trim();
      }

      await apiRequest('PUT', '/api/profile', payload);
      queryClient.setQueryData(['/api/profile'], (old: any) => ({
        ...(old || {}),
        ...payload,
        profileImageUrl: profilePictureUrl,
        isPhoneVerified: phoneChanged ? true : old?.isPhoneVerified,
      }));
      await queryClient.invalidateQueries({ queryKey: ['/api/profile'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast({
        title: 'Profile updated!',
        description: 'Your changes have been saved.',
      });
      setLocation('/profile');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Update Failed',
        description: getErrorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-lilac-300 border-t-purple-500" />
      </div>
    );
  }

  return (
    <OnboardingShell
      step={0}
      totalSteps={1}
      variant="bars"
      headerLabel="Edit Profile"
      onClose={close}
      onBack={close}
      onNext={save}
      nextLabel={saving ? 'Saving...' : 'Save Changes'}
      canProceed={!saving && canSavePhone}
    >
      <div className="space-y-5 pt-4">
        <div>
          <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">
            Profile Information
          </h1>
          <p className="text-deep-500 mt-2 text-sm">
            Name, age, gender, and avatar name are set during onboarding. You can update your phone
            (OTP required) and your photo.
          </p>
        </div>

        <ReadOnlyField label="First Name" value={firstName || '—'} />
        <ReadOnlyField label="Last Name" value={lastName || '—'} />
        <ReadOnlyField label="Display Avatar Name" value={avatarName || '—'} />
        <ReadOnlyField label="Age" value={age || '—'} />
        <ReadOnlyField label="Gender" value={gender || '—'} />

        <div>
          <p className="text-deep-700 text-sm mb-2 ml-1">Profile photo</p>
          <ImageUpload value={profilePictureUrl} onChange={onPhotoChange} useDataUrl />
        </div>

        <div>
          <p className="text-deep-700 text-sm mb-2 ml-1">Phone Number *</p>
          <input
            type="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setPhoneVerified(false);
            }}
            placeholder="+91…"
            className="w-full bg-white rounded-full px-5 py-3.5 text-deep-800 outline-none shadow-sm"
          />
          {phoneChanged && (
            <p className="text-xs text-deep-500 mt-2 ml-1">
              New number — verify with OTP before saving (one number per account).
            </p>
          )}
        </div>

        {phoneChanged && phone.trim().length >= 10 && (
          <PhoneVerification
            phoneNumber={phone.trim()}
            onVerified={() => {
              setPhoneVerified(true);
              setSavedPhone(phone.trim());
            }}
          />
        )}
      </div>
    </OnboardingShell>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-deep-700 text-sm mb-2 ml-1">{label}</p>
      <div className="w-full bg-deep-50 rounded-full px-5 py-3.5 text-deep-600 border border-deep-100">
        {value}
      </div>
    </div>
  );
}
